/**
 * CSD SDK 兼容层
 * 用于调用 CsdPublic 子库的功能
 * 提供 SDK 初始化、用户认证、用户信息获取等一系列功能
 */

import type { CSDClient } from '../../../CsdPublic/src/index'
import { createCSDClient } from '../../../CsdPublic/src/index'
import type { CSDConfig } from '../../../CsdPublic/src/types/config'

// ============================================
// 全局 CSD 客户端实例
// ============================================

let csdClientInstance: CSDClient | null = null

/**
 * 初始化 CSD SDK
 * @param config - SDK 配置
 * @returns CSD 客户端实例
 */
export async function initializeCSDSDK(config: CSDConfig): Promise<CSDClient> {
  if (csdClientInstance) {
    console.warn('[CSD Adapter] SDK 已初始化，返回现有实例')
    return csdClientInstance
  }

  try {
    // 创建 CSD 客户端
    csdClientInstance = createCSDClient(config)

    // 初始化 SDK（触发事件上报等）
    // 开发环境下捕获事件上报错误，避免 CORS 问题影响使用
    try {
      await csdClientInstance.initialize({
        enableEventReport: true
      })
    } catch (error) {
      // 事件上报失败不影响核心功能，仅记录警告
      console.warn('[CSD Adapter] 事件上报初始化失败（不影响核心功能）:', error)
    }

    console.log('[CSD Adapter] SDK 初始化成功')
    return csdClientInstance
  } catch (error) {
    console.error('[CSD Adapter] SDK 初始化失败:', error)
    throw error
  }
}

/**
 * 获取 CSD 客户端实例
 * @returns CSD 客户端实例，如果未初始化则返回 null
 */
export function getCSDClient(): CSDClient | null {
  return csdClientInstance
}

/**
 * 获取 CSD 客户端实例（必须已初始化）
 * @returns CSD 客户端实例
 * @throws 如果 SDK 未初始化则抛出错误
 */
export function getCSDClientOrThrow(): CSDClient {
  if (!csdClientInstance) {
    throw new Error('[CSD Adapter] SDK 未初始化，请先调用 initializeCSDSDK')
  }
  return csdClientInstance
}

// ============================================
// 认证相关功能
// ============================================

/**
 * 获取登录状态
 */
export function isLogin(): boolean {
  const client = getCSDClient()
  return client?.isLogin() ?? false
}

/**
 * 获取当前用户信息
 */
export function getUserInfo() {
  const client = getCSDClient()
  return client?.getUserInfo() ?? null
}

/**
 * 获取当前 Token
 */
export function getToken(): string {
  const client = getCSDClient()
  return client?.getToken() ?? ''
}

/**
 * 获取认证参数（用于 HTTP 请求头）
 * 返回设备信息、Token 等参数
 */
export function getAuthParams() {
  const client = getCSDClient()
  if (!client) {
    return {
      ekey: '',
      device_code: '',
      soft_id: '',
      token: '',
      source: 'default'
    }
  }

  // 从 SDK 获取认证参数
  const authService = client.getAuthService()
  const deviceStore = client.getDeviceStore()
  const sourceStore = client.getSourceStore()

  // 从存储中读取 invite_id
  let inviteId: number | undefined
  try {
    const inviteIdRaw = localStorage.getItem('mxai_invite_id')
    if (inviteIdRaw) {
      const parsed = parseInt(inviteIdRaw, 10)
      if (!Number.isNaN(parsed)) {
        inviteId = parsed
      }
    }
  } catch {
    // 忽略存储读取错误
  }

  return {
    ekey: deviceStore.ekey || '',
    device_code: deviceStore.deviceCode || '',
    soft_id: deviceStore.softId || '',
    token: authService.getToken() || '',
    source: sourceStore.source || 'default',
    qhclickid: sourceStore.qhclickid,
    bdVid: sourceStore.bdVid,
    strategy: sourceStore.strategy,
    mp: sourceStore.mp,
    invite_id: inviteId
  }
}

// ============================================
// 用户相关功能
// ============================================

/**
 * 获取用户信息（响应式）
 */
export function useUserInfo() {
  const client = getCSDClientOrThrow()
  return client.useUser()
}

/**
 * 获取用户积分信息（响应式）
 */
export function useUserPoints() {
  const client = getCSDClientOrThrow()
  const { pointsInfo } = client.useUser()
  return pointsInfo
}

/**
 * 获取用户 VIP 信息（响应式）
 */
export function useUserVIP() {
  const client = getCSDClientOrThrow()
  const { vipInfo } = client.useUser()
  return vipInfo
}

/**
 * 获取用户信息（异步 API 调用）
 * 从 CSD 服务器获取最新的用户信息
 */
export async function fetchUserInfo() {
  const client = getCSDClientOrThrow()
  const userService = client.userService
  try {
    const result = await userService.getUserInfo()
    console.log('[CSD Adapter] 用户信息获取成功:', result)
    return result
  } catch (error) {
    console.error('[CSD Adapter] 用户信息获取失败:', error)
    throw error
  }
}

/**
 * 刷新用户信息
 */
export async function refreshUserInfo(): Promise<void> {
  const client = getCSDClientOrThrow()
  const userService = client.getUserService()
  await userService.getUserInfo()
}

// ============================================
// 登录相关功能
// ============================================

/**
 * 获取二维码登录相关功能
 */
export function useQRLogin() {
  const client = getCSDClientOrThrow()
  return client.useQRLogin()
}

/**
 * 获取手机号登录相关功能
 */
export function usePhoneLogin() {
  const client = getCSDClientOrThrow()
  return client.usePhoneLogin()
}

/**
 * 获取认证相关功能（基础）
 */
export function useAuth() {
  const client = getCSDClientOrThrow()
  return client.useAuth()
}

/**
 * 登出
 */
export async function logout(): Promise<void> {
  const client = getCSDClientOrThrow()
  const authService = client.getAuthService()
  await authService.logout()
}

// ============================================
// 支付相关功能
// ============================================

/**
 * 获取用户积分（异步 API 调用）
 * 从 CSD 服务器获取当前用户的积分信息
 * 返回格式: { points: number, member: number, normal: number }
 */
export async function fetchUserTokens() {
  const client = getCSDClientOrThrow()
  const userService = client.getUserService()
  try {
    const result = await userService.getPointsInfo()
    console.log('[CSD Adapter] 用户积分获取成功:', result)
    return result
  } catch (error) {
    console.error('[CSD Adapter] 用户积分获取失败:', error)
    throw error
  }
}

/**
 * 获取支付相关功能
 */
export function usePayment() {
  const client = getCSDClientOrThrow()
  return client.usePayment()
}

/**
 * 获取支付流程相关功能
 */
export function usePaymentFlow() {
  const client = getCSDClientOrThrow()
  return client.usePaymentFlow()
}

// ============================================
// 邀请相关功能
// ============================================

/**
 * 获取邀请相关功能
 */
export function useInvite() {
  const client = getCSDClientOrThrow()
  return client.useInvite()
}

// ============================================
// 任务相关功能
// ============================================

/**
 * 获取任务轮询相关功能
 */
export function useTaskPolling() {
  const client = getCSDClientOrThrow()
  return client.useTaskPolling()
}

// ============================================
// 设备信息相关功能
// ============================================

/**
 * 获取设备信息 Store
 */
export function getDeviceStore() {
  const client = getCSDClientOrThrow()
  return client.getDeviceStore()
}

/**
 * 获取来源追踪 Store
 */
export function getSourceStore() {
  const client = getCSDClientOrThrow()
  return client.getSourceStore()
}

// ============================================
// 事件上报相关功能
// ============================================

/**
 * 获取事件上报服务
 */
export function getEventService() {
  const client = getCSDClientOrThrow()
  return client.getEventService()
}

// ============================================
// 导出类型
// ============================================

export type { CSDClient } from '../../../CsdPublic/src/index'
export type { CSDConfig } from '../../../CsdPublic/src/types/config'

// 导出 SDK 的登录状态回调设置方法
export { setLoginRequiredCallback, resetLoginRequiredFlag } from '../../../CsdPublic/src/index'

// 导出设备环境检测工具
export { isWeChat, isMobile, getDeviceEnvironment } from '../../../CsdPublic/src/core/utils/device'

/**
 * 微信 H5 授权跳转登录
 * @param callbackUrl 授权完成后的回调 URL
 */
export async function wechatLoginRedirect(callbackUrl: string): Promise<void> {
  const client = getCSDClientOrThrow()
  const authService = client.getAuthService()
  await authService.wechatLoginRedirect(callbackUrl)
}
